import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Inbox,
  Loader2,
  Minus,
  Plus,
  UtensilsCrossed,
} from 'lucide-react';
import { fetchRepasEvenement, fetchReservationRepas, reserverRepas } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { nomFamille, prenomUsage } from '../utils/nom';

import { dateFr } from '../utils/dates';
/** Le bon de réservation se télécharge sur l'API, pas via fetch : lien direct. */
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/** Date du service : c'est sur elle que le convive se présente. */
const serviceLe = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Réservation du repas d'un événement.
 *
 * Le parcours tient en un écran : combien de couverts, à quel nom, et on paie.
 * On ne demande pas de compte — un villageois n'a pas à s'inscrire pour venir
 * manger. Le paiement passe par Stripe Checkout, qui propose de lui-même Apple
 * Pay et Google Pay sur les appareils qui les portent.
 */
export default function ReservationRepas({ evenementId }: { evenementId: number }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const [form, setForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    commentaire: '',
  });
  // Les deux compteurs partent de zéro : le convive dit ce qu'il veut, il ne
  // corrige pas ce qu'on a décidé pour lui. Un couvert pré-coché se paie
  // parfois sans qu'on l'ait voulu.
  const [adultes, setAdultes] = useState(0);
  const [enfants, setEnfants] = useState(0);

  const { data, isPending, refetch } = useQuery({
    queryKey: ['repas', evenementId],
    queryFn: () => fetchRepasEvenement(evenementId),
  });

  // Retour de Stripe : le webhook a pu confirmer entre-temps, on relit l'état
  // pour que les couverts restants soient justes.
  const retour = params.get('repas');
  useEffect(() => {
    if (retour) refetch();
  }, [retour, refetch]);

  // Un membre connecté ne ressaisit pas ce que le comité connaît déjà.
  useEffect(() => {
    if (!user) return;
    setForm((precedent) => ({
      ...precedent,
      nom: precedent.nom || user.nom || user.fullName || '',
      prenom: precedent.prenom || user.prenom || '',
      email: precedent.email || user.email || '',
      telephone: precedent.telephone || user.telephone || '',
    }));
  }, [user]);

  const reserver = useMutation({
    mutationFn: () =>
      reserverRepas(evenementId, {
        nom: form.nom.trim(),
        prenom: form.prenom.trim() || null,
        email: form.email.trim(),
        telephone: form.telephone.trim() || null,
        commentaire: form.commentaire.trim() || null,
        nbAdultes: adultes,
        nbEnfants: enfants,
      }),
    onSuccess: (d) => {
      // Le paiement se poursuit chez Stripe ; la place est déjà retenue.
      window.location.href = d.url;
    },
    onError: (e: Error) => setErreur(e.message),
  });

  if (isPending) return null;

  const repas = data?.repas;
  if (!repas?.actif) return null;

  const total = adultes * repas.prixAdulte + enfants * repas.prixEnfant;
  const invalide =
    form.nom.trim() === '' || !form.email.includes('@') || adultes + enfants === 0;

  return (
    <section className="card lg:p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-amber-50 text-amber-700 shrink-0">
          <UtensilsCrossed className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h2 className="font-semibold lg:text-lg">Repas sur réservation</h2>
          {repas.date && (
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-0.5 first-letter:uppercase">
              {serviceLe.format(new Date(repas.date))}
            </p>
          )}
          {repas.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line mt-0.5">
              {repas.description}
            </p>
          )}
        </div>
      </div>

      {/* Retour de paiement */}
      {retour === 'succes' && <Confirmation reference={params.get('reference')} />}
      {retour === 'annule' && (
        <p className="text-sm rounded-xl px-3 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 dark:bg-gray-800">
          Paiement abandonné. Votre place n'a pas été retenue.
        </p>
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {repas.prixAdulte > 0 && (
          <span>
            Adulte <strong>{repas.prixAdulte} €</strong>
          </span>
        )}
        {repas.prixEnfant > 0 && (
          <span>
            Enfant <strong>{repas.prixEnfant} €</strong>
          </span>
        )}
        {repas.dateLimite && (
          <span className="text-gray-500 dark:text-gray-400">
            Réservations jusqu'au {dateFr(repas.dateLimite)}
          </span>
        )}
      </div>

      {/* Places restantes — l'information qui décide le visiteur. */}
      {!repas.complet && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {repas.restantTotal <= 10 ? (
            <span className="text-amber-700 font-medium">
              Plus que {repas.restantTotal} couvert{repas.restantTotal > 1 ? 's' : ''} disponible
              {repas.restantTotal > 1 ? 's' : ''}.
            </span>
          ) : (
            <>
              {repas.restantAdulte} couvert{repas.restantAdulte > 1 ? 's' : ''} adulte
              {repas.restantEnfant > 0 && <> et {repas.restantEnfant} enfant</>} encore disponibles.
            </>
          )}
        </p>
      )}

      {repas.complet ? (
        <button
          type="button"
          disabled
          className="w-full py-3 rounded-xl bg-gray-200 text-gray-500 font-semibold cursor-not-allowed dark:text-gray-400"
        >
          Complet
        </button>
      ) : !repas.ouvert ? (
        <button
          type="button"
          disabled
          className="w-full py-3 rounded-xl bg-gray-200 text-gray-500 font-semibold cursor-not-allowed dark:text-gray-400"
        >
          Réservations closes
        </button>
      ) : !ouvert ? (
        <button
          type="button"
          onClick={() => setOuvert(true)}
          className="btn-primary w-full justify-center py-3"
        >
          Réserver mon repas
        </button>
      ) : (
        <div className="space-y-3">
          <Compteur
            libelle="Repas adultes"
            prix={repas.prixAdulte}
            valeur={adultes}
            max={repas.restantAdulte}
            onChange={setAdultes}
          />
          <Compteur
            libelle="Repas enfants"
            prix={repas.prixEnfant}
            valeur={enfants}
            max={repas.restantEnfant}
            onChange={setEnfants}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              className="input"
              placeholder="Nom *"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: nomFamille(e.target.value) })}
            />
            <input
              className="input"
              placeholder="Prénom"
              value={form.prenom}
              onChange={(e) => setForm({ ...form, prenom: prenomUsage(e.target.value) })}
            />
            <input
              className="input"
              type="email"
              placeholder="E-mail *"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              className="input"
              type="tel"
              placeholder="Téléphone"
              value={form.telephone}
              onChange={(e) => setForm({ ...form, telephone: e.target.value })}
            />
          </div>

          <textarea
            className="input min-h-[4rem]"
            placeholder="Allergies, régime particulier, table souhaitée…"
            value={form.commentaire}
            onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
          />

          <p className="text-xs text-gray-500 dark:text-gray-400">
            La confirmation part à cette adresse : elle porte votre référence de réservation.
          </p>

          {erreur && (
            <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {erreur}
            </p>
          )}

          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            <span className="text-sm text-gray-600 dark:text-gray-400">Total</span>
            <span className="text-xl font-bold text-primary-700">{total} €</span>
          </div>

          <button
            type="button"
            onClick={() => {
              setErreur(null);
              setParams(new URLSearchParams());
              reserver.mutate();
            }}
            disabled={invalide || reserver.isPending || total <= 0}
            className="btn-primary w-full justify-center py-3 disabled:opacity-50"
          >
            {reserver.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Payer {total} € et réserver
          </button>

          <p className="text-xs text-gray-400 text-center">
            Paiement sécurisé par Stripe — carte bancaire, Apple&nbsp;Pay et Google&nbsp;Pay.
          </p>

          <button
            type="button"
            onClick={() => setOuvert(false)}
            className="text-sm text-gray-500 w-full text-center dark:text-gray-400"
          >
            Annuler
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Écran de retour de paiement.
 *
 * C'est le seul moment où le convive a la référence sous les yeux. Le message
 * la met en évidence, prévient que la confirmation peut tomber dans les
 * indésirables, et propose le bon de réservation en téléchargement : de quoi
 * repartir avec son code même si l'e-mail n'arrive jamais.
 */
function Confirmation({ reference }: { reference: string | null }) {
  // Le code de retrait n'est pas dans l'adresse de retour : on le relit sur la
  // référence. C'est lui que le convive présentera, la référence ne servant
  // qu'à se retrouver dans un échange avec le bureau.
  const { data } = useQuery({
    queryKey: ['reservation-repas', reference],
    queryFn: () => fetchReservationRepas(reference as string),
    enabled: reference !== null,
    retry: false,
  });

  const code = data?.reservation?.codeRetrait ?? null;

  return (
    <div className="rounded-2xl border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 p-4 lg:p-5 space-y-3">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-lg lg:text-xl font-semibold text-green-900 dark:text-green-100">
            Votre réservation est enregistrée
          </p>
          <p className="text-sm text-green-800/80 dark:text-green-200/80 mt-0.5">
            Merci, et à bientôt à table.
          </p>
        </div>
      </div>

      {(code || reference) && (
        <div className="rounded-xl bg-white dark:bg-gray-900 border border-green-200 dark:border-green-800 px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {code ? 'Votre code de retrait — notez-le' : 'Votre référence — notez-la'}
          </p>
          <p className="font-mono text-xl lg:text-2xl font-bold text-green-700 dark:text-green-400 tracking-wider">
            {code ?? reference}
          </p>
          {code && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              À présenter à l'accueil, avec ou sans QR code. Référence de la réservation :{' '}
              <span className="font-mono">{reference}</span>
            </p>
          )}
        </div>
      )}

      <p className="text-base text-green-900/90 dark:text-green-100/90 leading-relaxed">
        Un e-mail de confirmation vient de partir, avec votre code de retrait.
        <span className="block mt-1 flex items-start gap-1.5">
          <Inbox className="w-4 h-4 mt-1 shrink-0 opacity-70" />
          <span>
            S'il n'arrive pas, pensez à regarder dans vos <strong>indésirables</strong> ou
            vos <strong>spams</strong>.
          </span>
        </span>
      </p>

      {reference && (
        <a
          href={`${API_BASE}/repas/public/reservation/${encodeURIComponent(reference)}/bon.pdf`}
          className="btn-primary w-full justify-center py-3 inline-flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Télécharger mon bon de réservation
        </a>
      )}
    </div>
  );
}

/**
 * Sélecteur de quantité, borné par les couverts restants.
 *
 * Le plus s'éteint sur la dernière place disponible : mieux vaut l'empêcher
 * que refuser la réservation après la saisie du nom et de l'adresse.
 */
function Compteur({
  libelle,
  prix,
  valeur,
  max,
  onChange,
}: {
  libelle: string;
  prix: number;
  valeur: number;
  max: number;
  onChange: (v: number) => void;
}) {
  if (max === 0 && valeur === 0) return null;

  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 dark:border-gray-800">
      <div className="min-w-0">
        <p className="text-sm font-medium">{libelle}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {prix} € — {max} restant{max > 1 ? 's' : ''}
        </p>
      </div>

      <div className="inline-flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, valeur - 1))}
          disabled={valeur === 0}
          aria-label={`Retirer un ${libelle.toLowerCase()}`}
          className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center disabled:opacity-30"
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="w-8 text-center font-semibold tabular-nums">{valeur}</span>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, valeur + 1))}
          disabled={valeur >= max}
          aria-label={`Ajouter un ${libelle.toLowerCase()}`}
          className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center disabled:opacity-30"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
